import { NgClass } from '@angular/common';
import { Component, inject, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';

import { AppInsightsService } from '@k12/error_handler';
import { State } from '@k12/forms';
import { ProviderData, OwnerDetails, Employee, EmployeeSummary,  SeverityLevel, ToastMessageType, ProviderType } from '@k12/models';
import { ToastMessageService } from '@k12/notifications';
import { PhonePipe } from '@k12/pipes';
import { IUserRecordEmployeeService, USER_RECORD_EMPLOYEE_SERVICE } from '@k12/tokens';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'lib-provider-info',
  standalone: true,
  imports: [PhonePipe, NgClass],
  templateUrl: './provider-info.component.html'
})
export class ProviderInfoComponent implements OnInit {
  private _providerData: ProviderData = {};
  @Input() set providerData(value: ProviderData | null | undefined) {
    this._providerData = value ?? {};
  }
  get providerData(): ProviderData {
    return this._providerData;
  }

  private _ownerDetails: OwnerDetails = {};
  @Input() set ownerDetails(value: OwnerDetails | null | undefined) {
    this._ownerDetails = value ?? {};
  }
  get ownerDetails(): OwnerDetails {
    return this._ownerDetails;
  }
  @Input() states: State[] = [];
  @Input() showReviewStatus = false;

  public readonly ProviderType = ProviderType;

  private readonly appInsightsService = inject(AppInsightsService);
  private readonly employeeService = inject<IUserRecordEmployeeService>(USER_RECORD_EMPLOYEE_SERVICE, {
    optional: true
  });
  private readonly toastMessageService = inject(ToastMessageService);

  employeeCount: number = 0;

  get isTransportation(): boolean {
    return this.isProviderType(ProviderType.Transportation);
  }

  isProviderType(type: ProviderType): boolean {
    return (this.providerData.type ?? '').trim().toLowerCase() === type;
  }

  ngOnInit(): void {
    if (this.isProviderType(ProviderType.Therapy) || this.isTransportation) {
      this.getEmployeeCount();
    }
  }

  getAddressWithLineBreaks(address?: string | OwnerDetails['personalAddress']): string {
    // Handle cases where address is not provided
    if (!address) {
      return '-';
    }

    // If address is a string, process it normally
    if (typeof address === 'string') {
      return this.getAddress(address);
    }

    // If address is an object, try to construct a string from it
    const addressParts: string[] = [];

    // Add street address lines
    if ('address1' in address && address.address1) addressParts.push(String(address.address1));
    if ('address2' in address && address.address2) addressParts.push(String(address.address2));

    const cityStateZip: string[] = [];
    if ('city' in address && address.city) cityStateZip.push(String(address.city));
    if ('state' in address && address.state) {
      const stateLabel = this.stateLabelForAddress(address.state);
      if (stateLabel) {
        cityStateZip.push(stateLabel);
      }
    }
    if ('zip' in address && address.zip) cityStateZip.push(String(address.zip));

    if (cityStateZip.length > 0) {
      addressParts.push(cityStateZip.join(', '));
    }

    const addressString = addressParts.join('<br>');
    return addressString || '-';
  }

  get county(): string {
    return this.providerData.addresses?.[0]?.county || '-';
  }

  get employeeTotal(): number {
    return this.providerData.employees?.length ?? this.employeeCount ?? 0;
  }

  getPhoneExtension(phone: unknown): string {
    if (phone != null && typeof phone === 'object' && 'extension' in phone) {
      const extension = (phone as { extension?: unknown }).extension;
      const value = extension == null ? '' : String(extension).trim();
      return value || '-';
    }
    return '-';
  }

  get secondaryBusinessPhone(): string {
    const value = this.providerData.secondaryPhone || this.providerData.phonePrimary;
    return value != null && String(value).trim() !== '' ? String(value) : '';
  }

  get personalCounty(): string {
    const address = this.ownerDetails?.personalAddress;
    if (address && typeof address === 'object' && address.county) {
      return String(address.county);
    }
    return '-';
  }

  get displayedSsnLast4(): string {
    return this.formatSsnLast4(this.ownerDetails?.socialSecurityLast4);
  }

  get displayedEmail(): string {
    const email = this.ownerDetails?.email;
    return email && String(email).trim() !== '' ? String(email).trim() : '-';
  }

  formatDate(value?: string | Date): string {
    if (!value) return '-';

    const iso = value instanceof Date ? value.toISOString().slice(0, 10) : String(value).trim().slice(0, 10);
    const [year, month, day] = iso.split('-');
    return year && month && day ? `${month}/${day}/${year}` : '-';
  }

  formatSsnLast4(value?: string | number | null): string {
    if (value == null || value === '') return '-';
    const digits = String(value).replace(/\D/g, '');
    const last4 = digits.length >= 4 ? digits.slice(-4) : digits;
    return last4 ? `XXX-XX-${last4}` : '-';
  }

  getTransportationEmployeeLength(employees?: Employee[]): number {
    if (!employees?.length) {
      return 0;
    }
    return employees.filter((employee) => {
      const type = String(employee.employeeType ?? '').toLowerCase();
      return type === ProviderType.Transportation || type === 'transportation';
    }).length;
  }

  employeesSummaryLabel(): string {
    if (this.isTransportation) {
      return `Transportation (${this.getTransportationEmployeeLength(this.providerData.employees)})`;
    }
    return `Therapy (${this.getTherapyEmployeeLength(this.providerData.employees)}), Tutoring (${this.getTutoringEmployeeLength(this.providerData.employees)})`;
  }

  private stateLabelForAddress(state: string | { name?: string; code?: string } | null | undefined): string {
    if (state == null) return '';

    if (typeof state === 'object') {
      return this.getAddressCodeAndName(state) ?? '';
    }

    const str = String(state);
    const resolved = this.states.find((state) => state.code === str || state.name === str);
    return resolved?.code ?? str;
  }

  getSelectedIssuingState(): State | null {
    const value = this.ownerDetails?.issuingState || this.providerData.issuingState;
    if (!value) return null;
    return this.states.find((state) => state.code === value || state.name === value) || null;
  }

  getTherapyEmployeeLength(employees?: Employee[]): number {
    if (employees) {
      return employees.filter((employee) => employee.employeeType === 'Therapy').length;
    }
    return 0;
  }

  getTutoringEmployeeLength(employees?: Employee[]): number {
    if (employees) {
      return employees.filter((employee) => employee.employeeType === 'Tutor').length;
    }
    return 0;
  }

  /** Partner summary: owner / provider display name. */
  ownerDisplayName(): string {
    const ownerDetails = this.ownerDetails;
    const parts = [ownerDetails.firstName, ownerDetails.middleName, ownerDetails.lastName].filter(
      (part) => part != null && String(part).trim() !== ''
    );
    return parts.length ? parts.join(' ') : this.providerData.businessName || '-';
  }

  /** Sub services / subjects for display. */
  subServicesDisplay(): string {
    const subjects = this.providerData?.subjects?.filter((subject) => subject != null && String(subject).trim() !== '');
    if (subjects?.length) {
      return subjects.join(', ');
    }
    const names =
      this.providerData?.servicesProvided
        ?.map((service) => service?.name)
        .filter((name): name is string => name != null && name !== '') ?? [];
    return names.length ? names.join(', ') : '-';
  }

  serviceTypeLabel(): string {
    const type = this.providerData?.type;
    if (type === 'Tutor') return 'Tutoring';
    if (type === 'Transportation') return 'Transportation';
    return type || '-';
  }

  /** Personal / business-style address for partner card: lines + city, state name, zip, county. */
  partnerAddressHtml(address?: OwnerDetails['personalAddress']): string {
    if (address == null || address === '') return '-';
    if (typeof address === 'string') {
      return address ? address.replaceAll('\n', '<br>') : '-';
    }
    const lines: string[] = [];
    if (address.address1) lines.push(String(address.address1));
    if (address.address2) lines.push(String(address.address2));
    const tail: string[] = [];
    if (address.city) tail.push(String(address.city));
    const stateLabel = this.stateDisplayNameForAddress(address.state);
    if (stateLabel) tail.push(stateLabel);
    if (address.zip) tail.push(String(address.zip));
    if (address.county) tail.push(String(address.county));
    if (tail.length) lines.push(tail.join(', '));
    const html = lines.join('<br>');
    return html || '-';
  }

  private stateDisplayNameForAddress(state: string | { name?: string; code?: string } | null | undefined): string {
    if (state == null) return '';
    if (typeof state === 'object') {
      return this.getAddressCodeAndName(state) ?? '';
    }
    const str = String(state);
    const resolved = this.states.find((state) => state.code === str || state.name === str);
    return resolved?.name ?? str;
  }

  private getAddressCodeAndName(state: { name?: string; code?: string }): string | null {
    const code = state.code ? String(state.code) : '';
    const name = state.name ? String(state.name) : '';
    const fromCode = code ? this.states.find((state) => state.code === code) : null;
    if (fromCode) return fromCode.code;
    const fromName = name ? this.states.find((state) => state.name === name || state.code === name) : null;
    if (fromName) return fromName.code;
    return code || name;
  }

  private getEmployeeCount(): void {
    if (this.employeeService && this.providerData?.id) {
      this.employeeService.GetAllEmployeesSummaryAdmin(this.providerData.id).subscribe({
        next: (res: EmployeeSummary[]) => {
          this.employeeCount = res.length;
        },
        error: (error: unknown) => {
          const err = error instanceof Error ? error : new Error(String(error));
          this.appInsightsService.log(err, SeverityLevel.error);
          this.toastMessageService.showToast(ToastMessageType.Error, err.message);
        }
      });
    }
  }

  private getAddress(address: string): string {

    const plainAddress = address.replaceAll('<br>', '\n');
    const lines = plainAddress.split('\n');

    const lastLine = lines.at(-1) ?? '';
    const parts = lastLine.split(',');

    // city, county, state, zip
    if (parts.length >= 4) {
      const removeCounty = [
        parts[0].trim(), // city
        parts[2].trim(), // state
        parts[3].trim() // zip
      ].join(', ');

      lines.splice(-1, 1, removeCounty);
    }

    if (lines.length > 2) {
      lines.splice(1, 1); // remove address2
    }

    return lines.join('<br>');
  }
}
